import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Field } from "@/pages/Leads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Network } from "lucide-react";

export default function Franchises() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/franchises").then((r) => setItems(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <Button onClick={() => setOpen(true)} className="bg-navy text-white hover:bg-navy-hover" data-testid="new-franchise-btn"><Plus className="mr-2 h-4 w-4" /> Add Franchise</Button>
      </div>
      <div className="overflow-hidden rounded-xl border border-navy/10 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-cream/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3">Franchise</th>
              <th className="px-4 py-3">Owner</th>
              <th className="px-4 py-3">Territory</th>
              <th className="px-4 py-3">Revenue Share</th>
              <th className="px-4 py-3">Validity</th>
              <th className="px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">No franchises</td></tr>
            ) : items.map((f) => (
              <tr key={f.id} className="border-b last:border-0 hover:bg-nude/30" data-testid={`franchise-${f.id}`}>
                <td className="px-4 py-3"><div className="flex items-center gap-2 font-semibold text-ink"><Network className="h-4 w-4 text-gold" /> {f.name}</div></td>
                <td className="px-4 py-3 text-muted-foreground">{f.owner_name || "—"}</td>
                <td className="px-4 py-3 text-muted-foreground">{f.territory || "—"}</td>
                <td className="px-4 py-3 font-medium text-gold">{f.revenue_share}%</td>
                <td className="px-4 py-3 text-muted-foreground">{f.agreement_validity || "—"}</td>
                <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${f.status === "active" ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-600"}`}>{f.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <CreateFranchise open={open} onOpenChange={setOpen} onDone={load} />
    </div>
  );
}

function CreateFranchise({ open, onOpenChange, onDone }) {
  const empty = { name: "", owner_id: "", territory: "", agreement_validity: "", revenue_share: 15, status: "active" };
  const [form, setForm] = useState(empty);
  const [owners, setOwners] = useState([]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Eligible owners = users with the Franchisee Owner role.
  useEffect(() => {
    if (open) {
      api.get("/users", { params: { role: "franchisee_owner" } })
        .then((r) => setOwners(r.data)).catch(() => {});
    }
  }, [open]);

  const submit = async () => {
    if (!form.name) { toast.error("Franchise name required"); return; }
    try {
      await api.post("/franchises", {
        ...form,
        owner_id: form.owner_id || null,
        revenue_share: parseFloat(form.revenue_share) || 0,
      });
      toast.success("Franchise created"); setForm(empty); onOpenChange(false); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-display text-navy">Add Franchise</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <Field label="Name *"><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="franchise-name" /></Field>
          <Field label="Owner">
            <Select value={form.owner_id} onValueChange={(v) => set("owner_id", v)}>
              <SelectTrigger data-testid="franchise-owner"><SelectValue placeholder="Select a franchisee owner" /></SelectTrigger>
              <SelectContent>
                {owners.length === 0
                  ? <div className="px-2 py-1.5 text-xs text-muted-foreground">No franchisee-owner users. Create one in Access Control.</div>
                  : owners.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} <span className="text-muted-foreground">· {u.username}</span></SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Territory"><Input value={form.territory} onChange={(e) => set("territory", e.target.value)} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Revenue Share (%)"><Input type="number" value={form.revenue_share} onChange={(e) => set("revenue_share", e.target.value)} /></Field>
            <Field label="Agreement Validity"><Input type="date" value={form.agreement_validity} onChange={(e) => set("agreement_validity", e.target.value)} /></Field>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-navy text-white hover:bg-navy-hover" data-testid="save-franchise-btn">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
